import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import Modal from '../components/Modal';

// Pole Survey audit log (TKT-00042, 08/10/2026): who surveyed which address,
// when, the outcome they chose and how long it took, with the poles and drop
// paths they checked along the way. Read access is enforced by the API (Ashley
// Wass, Shane Grobler, Kevin du Plessis + full admins).

const ukDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const duration = (s) => {
  if (s == null) return '—';
  const n = Number(s);
  if (n < 60) return `${n}s`;
  const h = Math.floor(n / 3600); const m = Math.floor((n % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m ${String(n % 60).padStart(2, '0')}s`;
};
const VERDICT = { hoist: 'Hoist required', caution: 'Assess on site', climb: 'Climbable' };
const STATUS_TONE = { complete: 'bg-emerald-100 text-emerald-800', open: 'bg-amber-100 text-amber-800', cancelled: 'bg-gray-100 text-gray-500' };
const control = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function csvCell(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function SurveyDetail({ id, onClose }) {
  const { data: s, isLoading } = useQuery({
    queryKey: ['pole-survey', id],
    queryFn: async () => (await api.get(`/api/poles/surveys/${id}`)).data,
  });
  return (
    <Modal title={s ? s.address : 'Survey'} subtitle={s ? `${s.surveyor_name || 'Unknown'} · ${ukDateTime(s.started_at)}` : ''} onClose={onClose}>
      {isLoading || !s ? <p className="text-sm text-gray-400">Loading…</p> : (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm sm:grid-cols-4">
            <div><dt className="text-xs text-gray-500">Outcome</dt><dd className="font-semibold text-gray-900">{s.outcome_label || (s.status === 'cancelled' ? 'Cancelled' : 'In progress')}</dd></div>
            <div><dt className="text-xs text-gray-500">Duration</dt><dd className="font-semibold text-gray-900">{duration(s.duration_seconds)}</dd></div>
            <div><dt className="text-xs text-gray-500">Started</dt><dd className="text-gray-700">{ukDateTime(s.started_at)}</dd></div>
            <div><dt className="text-xs text-gray-500">Finished</dt><dd className="text-gray-700">{ukDateTime(s.finished_at)}</dd></div>
            {s.notes ? <div className="col-span-2 sm:col-span-4"><dt className="text-xs text-gray-500">Notes</dt><dd className="whitespace-pre-line text-gray-700">{s.notes}</dd></div> : null}
          </dl>

          <div className="rounded-lg border border-gray-200 bg-white">
            <p className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-900">What was checked ({s.events.length})</p>
            {s.events.length === 0 ? <p className="px-4 py-3 text-sm text-gray-500">Nothing was opened on the map during this survey.</p> : (
              <ul className="divide-y divide-gray-100 text-sm">
                {s.events.map((e) => (
                  <li key={e.id} className="flex gap-3 px-4 py-2">
                    <span className="w-12 shrink-0 text-xs text-gray-400">{new Date(e.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    {e.kind === 'pole' ? (
                      <span className="text-gray-700">
                        Pole <span className="font-medium text-gray-900">{e.detail.exchange} DP {e.detail.dp}/{e.detail.pole}</span>
                        {' · '}{VERDICT[e.detail.verdict] || e.detail.verdict || '—'}
                        {e.detail.result ? ` · ${e.detail.result}` : ''}
                        {e.detail.out_of_test ? ' · out of test' : ''}
                        {e.detail.cbt ? ` · ${e.detail.cbt}` : ''}
                      </span>
                    ) : (
                      <span className="text-gray-700">
                        Drop path <span className="font-medium text-gray-900">{e.detail.metres} m</span> from {e.detail.from}
                        {e.detail.headline ? ` · ${e.detail.headline}` : ''}
                        {e.detail.recommendation ? <span className="block text-xs text-gray-500">→ {e.detail.recommendation}</span> : null}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function PoleSurveyLog() {
  const [filters, setFilters] = useState({ q: '', surveyor: '', outcome: '', status: '', from: '', to: '' });
  const [openId, setOpenId] = useState(null);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const { data: meta } = useQuery({ queryKey: ['pole-survey-meta'], queryFn: async () => (await api.get('/api/poles/surveys/meta')).data });
  const { data, isLoading, error } = useQuery({
    queryKey: ['pole-surveys', filters],
    queryFn: async () => {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      return (await api.get('/api/poles/surveys', { params })).data.surveys;
    },
  });
  const rows = data || [];
  const surveyors = useMemo(() => {
    const m = new Map();
    for (const r of rows) if (!m.has(r.surveyor_entra_id)) m.set(r.surveyor_entra_id, r.surveyor_name || 'Unknown');
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const exportCsv = () => {
    const head = ['Address', 'Postcode', 'Surveyor', 'Status', 'Outcome', 'Started', 'Finished', 'Duration', 'Poles checked', 'Drop paths checked', 'Notes'];
    const lines = rows.map((r) => [r.address, r.postcode, r.surveyor_name, r.status, r.outcome_label, ukDateTime(r.started_at),
      ukDateTime(r.finished_at), duration(r.duration_seconds), r.poles_checked, r.paths_checked, r.notes].map(csvCell).join(','));
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `pole-survey-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-4 sm:p-6">
      <Link to="/pole-survey" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Pole Survey
      </Link>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Survey log</h1>
          <p className="text-sm text-gray-500">Every address surveyed on the Pole Survey map: who, when, the outcome and how long it took.</p>
        </div>
        <button type="button" onClick={exportCsv} disabled={!rows.length} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input value={filters.q} onChange={(e) => set('q', e.target.value)} placeholder="Address or postcode…" className={`${control} w-full sm:w-64`} />
        <select value={filters.surveyor} onChange={(e) => set('surveyor', e.target.value)} className={control}>
          <option value="">Any surveyor</option>
          {surveyors.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select value={filters.outcome} onChange={(e) => set('outcome', e.target.value)} className={control}>
          <option value="">Any outcome</option>
          {Object.entries(meta?.outcomes || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => set('status', e.target.value)} className={control}>
          <option value="">Any status</option>
          <option value="complete">Complete</option>
          <option value="open">In progress</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-gray-500">From <input type="date" value={filters.from} onChange={(e) => set('from', e.target.value)} className={control} /></label>
        <label className="flex items-center gap-1 text-xs text-gray-500">To <input type="date" value={filters.to} onChange={(e) => set('to', e.target.value)} className={control} /></label>
        <span className="ml-auto text-xs text-gray-400">{rows.length} survey{rows.length === 1 ? '' : 's'}</span>
      </div>

      {error ? (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error.response?.data?.error || 'Could not load the survey log.'}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2.5">Address</th><th className="px-4 py-2.5">Surveyor</th><th className="px-4 py-2.5">Outcome</th>
                <th className="px-4 py-2.5">Started</th><th className="px-4 py-2.5 text-right">Duration</th><th className="px-4 py-2.5 text-right">Checked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr> : null}
              {!isLoading && !rows.length ? <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No surveys match.</td></tr> : null}
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setOpenId(r.id)} className="cursor-pointer hover:bg-brand-light/40">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{r.address}</td>
                  <td className="px-4 py-2.5 text-gray-700">{r.surveyor_name || '—'}</td>
                  <td className="px-4 py-2.5">
                    {r.status === 'complete' ? <span className="text-gray-800">{r.outcome_label}</span>
                      : <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[r.status]}`}>{r.status === 'open' ? 'In progress' : 'Cancelled'}</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">{ukDateTime(r.started_at)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{duration(r.duration_seconds)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs text-gray-500">{r.poles_checked} pole{Number(r.poles_checked) === 1 ? '' : 's'} · {r.paths_checked} path{Number(r.paths_checked) === 1 ? '' : 's'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId ? <SurveyDetail id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}
